import { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { CurrentSubscriptionStatus } from "@client/resources/CurrentSubscriptionResource";
import { Spinner } from "@components/Spinner";
import { useClinicSubscription } from "@hooks";
import { useFetchDietitianAccountQuery } from "@hooks/queries/settings";
import { Roles } from "@utils/settings";

import {
  PLANS_AND_PAYMENTS,
  PRICING_CONFIGURE,
  PRICING,
  PRICING_SERVICE_CONFIGURE,
  PLANS_AND_PAYMENTS_INVOICES,
} from "@routes";
import { DASHBOARD } from "./routes";

export const ProtectedRoute = (props: ProtectedRouteProps) => {
  const { children, allowedUserTypes } = props;
  const { account, isLoading, isError } = useFetchDietitianAccountQuery();
  const {
    hasSubscription,
    hasValidStatus,
    isLoading: isSubscriptionLoading,
  } = useClinicSubscription();
  const { pathname } = useLocation();

  /** helpers */
  const isAuthenticated = account && !isLoading;
  const isUserTypeAllowed =
    account &&
    allowedUserTypes &&
    allowedUserTypes.some(role => account.roles.includes(role));

  /** check if user is loading */
  if (isLoading || isSubscriptionLoading)
    return <Spinner className="h-screen" />;

  /** if user not authenticated - redirect to login page */
  if (!isAuthenticated || isError) return <Spinner className="h-screen" />;

  /** if user is not allowed to visit this page - redirect to homepage */
  if (!isUserTypeAllowed) return <Navigate to={DASHBOARD} />;

  if (
    !ALLOWED_PATHS.includes(pathname) &&
    (!hasSubscription || !hasValidStatus)
  ) {
    return <Navigate to={PLANS_AND_PAYMENTS} />;
  }
  /** if user is allowed and has permissions - redirect to page */
  return children ? <>{children}</> : <Outlet />;
};

interface ProtectedRouteProps {
  /** child components such as layouts */
  children?: ReactNode | ReactNode[];
  /** list of allowed user typeDefinitions to visit this route */
  allowedUserTypes?: Roles[];
}

const ALLOWED_PATHS = [
  PLANS_AND_PAYMENTS,
  PRICING,
  PRICING_CONFIGURE,
  PRICING_SERVICE_CONFIGURE,
  PLANS_AND_PAYMENTS_INVOICES,
];

const ALLOWED_CLINIC_STATUSES = [
  CurrentSubscriptionStatus.Active,
  CurrentSubscriptionStatus.Trialing,
];
